import { useReducer, useCallback } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { SettingsSubmenuMemo } from '../components/settings/SettingsSubmenu';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import { BusinessProfileView } from './BusinessProfileView';
import { BusinessProfileEditForm } from './BusinessProfileEditForm';
import { DeleteBusinessDialog } from './DeleteBusinessDialog';
import { INDUSTRIES } from './businessSettingsConstants';
import type { IBusinessFormData } from '@/@types/businessSettings';

interface IBusinessSettingsState {
  isEditing: boolean;
  isDeleting: boolean;
  isGenerating: boolean;
  showDeleteDialog: boolean;
  showScrapeError: boolean;
  error: string | null;
  successMessage: string | null;
  isSaving: boolean;
  formData: IBusinessFormData;
}

type BusinessSettingsAction =
  | { type: 'START_EDIT'; formData: IBusinessFormData }
  | { type: 'CANCEL_EDIT' }
  | { type: 'SET_FORM_DATA'; formData: IBusinessFormData }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_SUCCESS'; message: string | null }
  | { type: 'SET_SAVING'; value: boolean }
  | { type: 'SET_DELETING'; value: boolean }
  | { type: 'SET_DELETE_DIALOG'; show: boolean }
  | { type: 'SET_GENERATING'; value: boolean }
  | { type: 'GENERATE_SUCCESS'; description: string }
  | { type: 'GENERATE_ERROR' }
  | { type: 'DISMISS_SCRAPE_ERROR' }
  | { type: 'SAVE_SUCCESS'; message: string }
  | { type: 'DELETE_SUCCESS'; message: string };

function businessSettingsReducer(
  state: IBusinessSettingsState,
  action: BusinessSettingsAction
): IBusinessSettingsState {
  switch (action.type) {
    case 'START_EDIT':
      return {
        ...state,
        isEditing: true,
        error: null,
        successMessage: null,
        formData: action.formData,
      };
    case 'CANCEL_EDIT':
      return { ...state, isEditing: false, error: null, successMessage: null };
    case 'SET_FORM_DATA':
      return { ...state, formData: action.formData };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'SET_SUCCESS':
      return { ...state, successMessage: action.message };
    case 'SET_SAVING':
      return { ...state, isSaving: action.value };
    case 'SET_DELETING':
      return { ...state, isDeleting: action.value };
    case 'SET_DELETE_DIALOG':
      return { ...state, showDeleteDialog: action.show };
    case 'SAVE_SUCCESS':
      return { ...state, successMessage: action.message, isEditing: false, isSaving: false };
    case 'SET_GENERATING':
      return { ...state, isGenerating: action.value };
    case 'GENERATE_SUCCESS':
      return {
        ...state,
        isGenerating: false,
        formData: { ...state.formData, description: action.description },
      };
    case 'GENERATE_ERROR':
      return { ...state, isGenerating: false, showScrapeError: true };
    case 'DISMISS_SCRAPE_ERROR':
      return { ...state, showScrapeError: false };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        successMessage: action.message,
        showDeleteDialog: false,
        isDeleting: false,
      };
  }
}

const INITIAL_BUSINESS_STATE: IBusinessSettingsState = {
  isEditing: false,
  isDeleting: false,
  isGenerating: false,
  showDeleteDialog: false,
  showScrapeError: false,
  error: null,
  successMessage: null,
  isSaving: false,
  formData: {
    name: '',
    brandName: '',
    industry: '',
    customIndustry: '',
    websiteUrl: '',
    description: '',
    timezone: 'Europe/Nicosia',
  },
};

export const BusinessSettingsPage = () => {
  const business = useQuery(api.businesses.queries.getCurrentBusiness);
  const updateBusiness = useMutation(api.businesses.mutations.updateBusiness);
  const deleteBusiness = useMutation(api.businesses.mutations.deleteBusiness);
  const createCrawlSession = useMutation(api.crawlSessions.create);
  const crawlWebsite = useAction(api.integrations.brightdata.crawlWebsiteMultiPage);
  const generateDescription = useAction(api.ai.descriptionGenerator.generateWithRetryAction);

  const [state, dispatch] = useReducer(businessSettingsReducer, INITIAL_BUSINESS_STATE);

  const handleEdit = useCallback(() => {
    if (business) {
      const isCustomIndustry = !INDUSTRIES.some(ind => ind.value === business.industry);
      dispatch({
        type: 'START_EDIT',
        formData: {
          name: business.name || '',
          brandName: business.brandName || '',
          industry: isCustomIndustry ? 'other' : business.industry,
          customIndustry: isCustomIndustry ? business.industry : '',
          websiteUrl: business.websiteUrl || '',
          description: business.description || '',
          timezone: business.timezone ?? 'Europe/Nicosia',
        },
      });
    }
  }, [business]);

  const handleCancel = useCallback(() => {
    dispatch({ type: 'CANCEL_EDIT' });
  }, []);

  const handleSave = useCallback(async () => {
    if (!business) return;
    if (!state.formData.name.trim()) {
      dispatch({ type: 'SET_ERROR', error: 'Business name is required' });
      return;
    }
    if (!state.formData.industry) {
      dispatch({ type: 'SET_ERROR', error: 'Industry is required' });
      return;
    }
    if (state.formData.industry === 'other' && !state.formData.customIndustry.trim()) {
      dispatch({ type: 'SET_ERROR', error: 'Please enter your industry' });
      return;
    }

    dispatch({ type: 'SET_SAVING', value: true });
    dispatch({ type: 'SET_ERROR', error: null });

    try {
      const industryValue =
        state.formData.industry === 'other'
          ? state.formData.customIndustry.trim()
          : state.formData.industry;

      await updateBusiness({
        businessId: business._id,
        name: state.formData.name.trim(),
        brandName: state.formData.brandName.trim() || undefined,
        industry: industryValue,
        websiteUrl: state.formData.websiteUrl.trim() || undefined,
        description: state.formData.description.trim() || undefined,
        descriptionSource: state.formData.description.trim() ? 'manual' : undefined,
        timezone: state.formData.timezone || 'Europe/Nicosia',
      });

      dispatch({ type: 'SAVE_SUCCESS', message: 'Business profile updated successfully!' });
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        error: err instanceof Error ? err.message : 'Failed to update profile',
      });
    } finally {
      dispatch({ type: 'SET_SAVING', value: false });
    }
  }, [business, state.formData, updateBusiness]);

  const handleGenerateDescription = useCallback(async () => {
    if (!business) return;
    const rawUrl = state.formData.websiteUrl.trim();
    if (!rawUrl) return;

    const websiteUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;

    dispatch({ type: 'SET_GENERATING', value: true });
    dispatch({ type: 'SET_ERROR', error: null });

    try {
      const sessionId = await createCrawlSession({
        businessId: business._id,
        websiteUrl,
      });

      const crawledPages = await crawlWebsite({
        sessionId,
        websiteUrl,
      });

      const result = await generateDescription({
        businessId: business._id,
        crawledPages,
      });

      dispatch({ type: 'GENERATE_SUCCESS', description: result.description });
    } catch {
      dispatch({ type: 'GENERATE_ERROR' });
    }
  }, [business, state.formData.websiteUrl, createCrawlSession, crawlWebsite, generateDescription]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!business) return;
    dispatch({ type: 'SET_DELETING', value: true });
    dispatch({ type: 'SET_ERROR', error: null });
    try {
      await deleteBusiness({ businessId: business._id });
      dispatch({ type: 'DELETE_SUCCESS', message: 'Business profile deleted successfully!' });
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        error: err instanceof Error ? err.message : 'Failed to delete profile',
      });
    } finally {
      dispatch({ type: 'SET_DELETING', value: false });
    }
  }, [business, deleteBusiness]);

  const getIndustryLabel = (industryValue: string) => {
    const found = INDUSTRIES.find(ind => ind.value === industryValue);
    return found ? found.label : industryValue;
  };

  const isLoading = business === undefined;
  const hasBusiness = business !== null && business !== undefined;

  const mainContent = (
    <div className="container mx-auto max-w-3xl py-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Business Settings</h1>
          <p className="mt-2 text-muted-foreground">
            Manage your business profile for AI content generation
          </p>
        </div>

        {state.successMessage && (
          <div className="rounded-md bg-primary border border-primary p-4 text-sm text-primary-foreground">
            {state.successMessage}
          </div>
        )}

        {state.error && (
          <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
            {state.error}
          </div>
        )}

        {isLoading && (
          <div className="rounded-lg border p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-2">Loading profile...</p>
          </div>
        )}

        {!isLoading && !hasBusiness && (
          <div className="rounded-lg border p-8 text-center">
            <p className="text-muted-foreground">
              No business profile found. Please complete the onboarding process.
            </p>
            <Button className="mt-4" onClick={() => (window.location.href = '/onboarding')}>
              Go to Onboarding
            </Button>
          </div>
        )}

        {hasBusiness && !state.isEditing && (
          <BusinessProfileView
            business={business}
            onEdit={handleEdit}
            onDelete={() => dispatch({ type: 'SET_DELETE_DIALOG', show: true })}
            getIndustryLabel={getIndustryLabel}
          />
        )}

        {hasBusiness && state.isEditing && (
          <BusinessProfileEditForm
            formData={state.formData}
            setFormData={formData => dispatch({ type: 'SET_FORM_DATA', formData })}
            isSaving={state.isSaving}
            isGenerating={state.isGenerating}
            onSave={handleSave}
            onCancel={handleCancel}
            onGenerate={handleGenerateDescription}
          />
        )}

        {state.showDeleteDialog && (
          <DeleteBusinessDialog
            businessName={business?.name ?? ''}
            isDeleting={state.isDeleting}
            onConfirm={handleDeleteConfirm}
            onCancel={() => dispatch({ type: 'SET_DELETE_DIALOG', show: false })}
          />
        )}

        <AlertDialog
          open={state.showScrapeError}
          onOpenChange={() => dispatch({ type: 'DISMISS_SCRAPE_ERROR' })}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Could not generate description</AlertDialogTitle>
              <AlertDialogDescription>
                We couldn&apos;t scrape your website automatically. Please write your business
                description manually.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => dispatch({ type: 'DISMISS_SCRAPE_ERROR' })}>
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );

  return {
    submenu: <SettingsSubmenuMemo />,
    mainContent,
  };
};
